import { useEffect, useState } from 'react'
import { GameBoard } from '@/components/GameBoard'
import { LetterRack } from '@/components/LetterRack'
import { ScoreDisplay } from '@/components/ScoreDisplay'
import { PlayToggle } from '@/components/PlayToggle'
import { Results } from '@/components/Results'
import type { CommunityStats } from '@/lib/stats'
import { Archive } from '@/components/Archive'
import { Brand } from '@/components/Brand'
import { DragGhost } from '@/components/DragGhost'
import { ThemePicker } from '@/components/ThemePicker'
import { useGameState } from '@/hooks/useGameState'
import { useKeyboard } from '@/hooks/useKeyboard'
import { bestScore, hasFinishedBefore, loadDay, todayUTC } from '@/lib/history'
import { fetchPuzzle, formatDate, requestedDate } from '@/lib/puzzles'
import { loadWords } from '@/lib/words'

type LoadState = { status: 'loading' } | { status: 'ready' } | { status: 'error'; message: string }

export function GamePage() {
  const { puzzle, record, start, setDictionary, markSubmitted, markStartedSent } = useGameState()
  const [load, setLoad] = useState<LoadState>({ status: 'loading' })
  const [stats, setStats] = useState<CommunityStats | null>(null)
  // The day whose results the player closed; results open by themselves once a day is finished.
  const [resultsClosedFor, setResultsClosedFor] = useState<string | null>(null)
  // Bumped when the results are reopened, to fetch fresh stats: percentiles move as more people play.
  const [reopened, setReopened] = useState(0)
  const [showHelp, setShowHelp] = useState(false)
  const [showArchive, setShowArchive] = useState(false)
  useKeyboard(load.status === 'ready' && !showArchive)

  useEffect(() => {
    fetchPuzzle(requestedDate())
      .then((p) => {
        const day = loadDay(p.date)
        start(p, day)
        setShowHelp(day.attempts.length === 0 && !localStorage.getItem('wordevery.seenHelp'))
        setLoad({ status: 'ready' })
      })
      .catch((err: Error) => setLoad({ status: 'error', message: err.message }))
    loadWords()
      .then(setDictionary)
      .catch(() => useGameState.setState({ message: 'Could not load the word list. Check your connection and reload.' }))
  }, [start, setDictionary])

  const date = record?.date
  // Archive plays still join that day's scoreboard; only "started" is today-only.
  const isToday = date === todayUTC()
  // "Started" means the player found a valid placement, whether or not they played it yet.
  const hasPlayed = (record?.attempts.length ?? 0) > 0 || Boolean(record?.bestFound)
  const startedSent = record?.startedSent ?? false

  useEffect(() => {
    if (!hasPlayed || startedSent || !date || !isToday) return
    const controller = new AbortController()
    fetch(`/api/events/${date}`, {
      signal: controller.signal,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: 'started' }),
    })
      .then((res) => {
        if (res.ok || res.status === 429) markStartedSent()
      })
      .catch(() => {
        // Offline: sent next time the game is opened.
      })
    return () => controller.abort()
  }, [hasPlayed, startedSent, date, isToday, markStartedSent])

  const finished = record?.finished ?? false
  const submitted = record?.submitted ?? false
  const myBest = record ? bestScore(record) : 0
  const showResults = finished && resultsClosedFor !== date

  useEffect(() => {
    if (!finished || !date) return
    const controller = new AbortController()
    const request = submitted
      ? fetch(`/api/stats/${date}`, { signal: controller.signal })
      : fetch(`/api/stats/${date}`, {
          signal: controller.signal,
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ score: myBest, returning: isToday && hasFinishedBefore(date) }),
        })
    request
      .then(async (res) => {
        if (res.status === 429) {
          // Rate limited (e.g. a shared network): stop retrying and just show stats.
          markSubmitted()
          return
        }
        if (!res.ok) return
        if (!submitted) markSubmitted()
        setStats((await res.json()) as CommunityStats)
      })
      .catch(() => {
        // Offline: the score is sent next time the game is opened.
      })
    return () => controller.abort()
  }, [finished, submitted, date, isToday, myBest, markSubmitted, reopened])

  const closeHelp = () => {
    setShowHelp(false)
    try {
      localStorage.setItem('wordevery.seenHelp', '1')
    } catch {
      // Ignore.
    }
  }

  return (
    // Exactly one screen tall: the board takes whatever height the rest leaves, so
    // the game never scrolls. With the help open it may, inside this container.
    <div className="page mx-auto flex h-dvh max-w-5xl flex-col overflow-y-auto px-3 pt-3 pb-3 sm:px-6 sm:pt-6">
      <header className="mb-4 flex items-center justify-between gap-2 sm:mb-6">
        <div className="min-w-0">
          <Brand />
          <p className="muted text-sm">
            {formatDate(puzzle?.date ?? todayUTC(), { weekday: 'long', month: 'long', day: 'numeric' })}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          <button type="button" onClick={() => setShowArchive(true)} aria-label="Archive" className="btn-secondary px-2.5 py-2 text-sm sm:px-3">
            <svg aria-hidden viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" className="size-5 sm:mr-1.5 sm:size-4">
              <rect x="3" y="4" width="14" height="13" rx="2" />
              <path d="M3 8h14M7 2.5v3M13 2.5v3" />
            </svg>
            <span className="hidden sm:inline">Archive</span>
          </button>
          <ThemePicker />
          <button
            type="button"
            onClick={() => setShowHelp((v) => !v)}
            className="btn-secondary px-2.5 py-2 text-sm sm:px-3"
            aria-expanded={showHelp}
            aria-label="How to play"
          >
            <span aria-hidden className="flex size-5 items-center justify-center font-black sm:hidden">
              ?
            </span>
            <span className="hidden sm:inline">How to play</span>
          </button>
        </div>
      </header>

      {load.status === 'ready' && !isToday && (
        <p className="callout mb-3 flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-sm">
          {requestedDate() ? (
            <>
              <span>Archive puzzle: your score joins this day&apos;s scoreboard but not your streak.</span>
              <a href="/" className="font-semibold underline-offset-2 hover:underline">
                Play today&apos;s puzzle →
              </a>
            </>
          ) : (
            // The API stood in an earlier puzzle because today's isn't published.
            <span>Today&apos;s puzzle isn&apos;t out yet, so here&apos;s the most recent one. Your score joins that day&apos;s scoreboard.</span>
          )}
        </p>
      )}

      {showHelp && (
        <section className="panel mb-3 p-4 text-sm">
          <ul className="list-disc space-y-1 pl-5">
            <li>Make the highest-scoring play you can with your seven letters.</li>
            <li>
              Tap a letter, then tap a square (or drag it). Tap a placed tile to take it back. Drop a tile on one of yours
              to swap them, and drag letters along the rack to rearrange them.
            </li>
            <li>
              On a keyboard: click a square and type. Space switches between across and down, arrows move, Backspace
              takes a tile back and Enter plays. Shift+arrows move the focused rack letter.
            </li>
            <li>Tiles go in one row or column, must connect to a word on the board, and every word formed must be real.</li>
            <li>
              <strong>2L</strong>/<strong>3L</strong> double or triple a letter, <strong>2W</strong>/<strong>3W</strong> double or
              triple the word.
              Using all seven letters adds 50.
            </li>
            <li>
              You get one play, so look around first: your best placement so far is kept, and &ldquo;Best so far&rdquo; puts it
              back. Play asks you to confirm before it counts.
            </li>
          </ul>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:flex">
            <a href="/?tutorial" className="btn-primary">
              Show me how
            </a>
            <button type="button" onClick={closeHelp} className="btn-secondary">
              Got it
            </button>
          </div>
        </section>
      )}

      {load.status === 'loading' && <p className="muted py-16 text-center">Loading today&apos;s puzzle…</p>}

      {load.status === 'error' && (
        <div className="panel mx-auto mt-10 max-w-sm p-6 text-center">
          <p>{load.message}</p>
          <button type="button" onClick={() => window.location.reload()} className="btn-primary mt-4">
            Try again
          </button>
        </div>
      )}

      {load.status === 'ready' && (
        <main className="flex min-h-0 flex-1 flex-col gap-3 md:grid md:grid-cols-[minmax(0,1fr)_22rem] md:gap-10">
          <div className="flex flex-1 flex-col gap-3">
            <div className="md:hidden">
              <ScoreDisplay />
            </div>
            <GameBoard />
          </div>
          <div className="shrink-0 space-y-3">
            <div className="hidden md:block">
              <ScoreDisplay />
            </div>
            <LetterRack />
            {finished && !showResults && (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setResultsClosedFor(null)
                    setReopened((n) => n + 1)
                  }}
                  className="btn-primary"
                >
                  Results
                </button>
                <PlayToggle />
              </div>
            )}
          </div>
        </main>
      )}

      {showResults && <Results stats={stats} onClose={() => setResultsClosedFor(date ?? null)} />}
      {showArchive && <Archive onClose={() => setShowArchive(false)} />}
      <DragGhost />
    </div>
  )
}
